import React from 'react';
import { Helmet } from '@plone/volto/helpers';
import { useSiteConfig } from '../../helpers/useSiteConfig';

/**
 * News Item `<title>` suffix (returning-visitor fix, BL-0099).
 *
 * Every document page ends its `<title>` with " | <site.seoName>" (see
 * DocumentSEO.titleWithDocid + the `title` build below it) — a returning
 * visitor with several tabs open, or a browser history / bookmark list, can
 * tell which archive a document tab belongs to. News Item pages never got
 * that: core's `NewsItemView` carries no SEO component of its own, so the
 * `<title>` a News Item renders is whatever core's `ContentMetadataTags`
 * falls back to — the bare stored title, no suffix. Confirmed on the live
 * build 2026-09-27:
 *
 *   curl .../news/published-doe-o-142-3c-unclassified-foreign-national-access-program
 *   -> <title>Published: DOE O 142.3C, Unclassified Foreign National Access Program</title>
 *
 * against a document on the same site:
 *
 *   curl .../documents/cm-certification-for-doe-p-4561-2
 *   -> <title>CM, Certification for DOE P 456.1 | DOE Directives Archive</title>
 *
 * This mounts inside the News Item detail view (see ../NewsView/NewsItemView),
 * which — like DocumentView/DocumentSEO — renders below core's
 * `ContentMetadataTags` in the tree; react-helmet keeps the LAST declared
 * `<title>`, so this wins without patching core.
 *
 * `og:title` is deliberately left alone: DocumentSEO does not suffix it
 * either (it emits `headline`, the un-suffixed heading), and core's
 * `ContentMetadataTags` already emits the correct un-suffixed `og:title` for
 * News Items — nothing here re-declares it, so that tag is untouched.
 */
export const newsTitleWithSite = (title, seoName) => {
  const t = title ? String(title).trim() : '';
  const s = seoName ? String(seoName).trim() : '';
  if (!s) return t;
  if (!t) return s;
  return `${t} | ${s}`;
};

const NewsSEO = ({ content }) => {
  const site = useSiteConfig(content);
  if (!content) return null;
  const title = newsTitleWithSite(content.title, site.seoName);
  if (!title) return null;
  return (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );
};

export default NewsSEO;
